import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ForecastConfidenceBucket {
  count: number;
  avg_error_kwh: number | null;
}

export interface ForecastFleetAggregate {
  total_forecasts: number;
  avg_error_kwh: number | null;
  avg_error_pct: number | null;
  median_error_kwh: number | null;
  by_confidence: {
    high: ForecastConfidenceBucket;
    medium: ForecastConfidenceBucket;
    low: ForecastConfidenceBucket;
  };
}

export interface ForecastPropertyRow {
  property_id: string;
  property_name: string;
  org_name: string;
  org_id: string;
  forecast_count: number;
  avg_error_kwh: number | null;
  avg_error_pct: number | null;
}

export interface ForecastOrg {
  id: string;
  name: string;
  property_count: number;
}

export interface ForecastAccuracyData {
  fleetAggregate: ForecastFleetAggregate;
  perProperty: ForecastPropertyRow[];
  orgs: ForecastOrg[];
}

async function fetchForecastAccuracy(lookbackDays: number, orgId: string | null): Promise<ForecastAccuracyData> {
  const { data, error } = await supabase.rpc('admin_forecast_accuracy', {
    p_lookback_days: lookbackDays,
    p_org_id: orgId,
  });
  if (error) throw error;

  const d = data as { fleet_aggregate: ForecastFleetAggregate; per_property: ForecastPropertyRow[]; orgs: ForecastOrg[] };
  return {
    fleetAggregate: d.fleet_aggregate,
    perProperty: d.per_property ?? [],
    orgs: d.orgs ?? [],
  };
}

export function useAdminForecastAccuracy(
  enabled: boolean,
  refetchInterval: number | false,
  lookbackDays: number,
  orgId: string | null
) {
  return useQuery({
    queryKey: ['admin-forecast-accuracy', lookbackDays, orgId],
    queryFn: () => fetchForecastAccuracy(lookbackDays, orgId),
    enabled,
    refetchInterval,
  });
}
